import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { getAdmFileUrl } from '../../utils/admProgress';
import {
  Box, Typography, Button, Stack, CircularProgress, Alert, Snackbar,
  Card, CardContent, Paper, Chip, TextField, MenuItem, FormControl,
  IconButton, Tooltip, useMediaQuery, Divider, Collapse
} from '@mui/material';
import {
  Refresh, RequestQuote, CheckCircle, PendingActions, LocalShipping,
  Person, LocationOn, DoneAll, Receipt, ArrowForward, Payments,
  AccountBalance, Visibility, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getMonitoringPenagihan, updatePembayaran } from '../API/supplierPenyedia';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const formatRp = (n) => (!n && n !== 0) ? '-' : 'Rp ' + Number(n).toLocaleString('id-ID');
const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const BAYAR_COLOR = { 'Belum Dibayar': 'error', 'Sudah Dibayar': 'success' };
const TAGIH_COLOR = { 'Belum Ditagih': 'default', 'Ditagihkan': 'warning', 'Lunas': 'success' };
const PENGIRIMAN_COLOR = { 'Menunggu Respon': 'default', 'Diproses': 'info', 'Dikirim': 'warning', 'Sampai': 'success' };

const MonitoringPenagihanSupplier = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:800px)');

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedAdm, setExpandedAdm] = useState(null);
  const notify = (m, s = 'success') => setSnackbar({ open: true, message: m, severity: s });

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await getMonitoringPenagihan(params);
      setList(res.data?.data || []);
    } catch (e) {
      notify(e.response?.data?.message || 'Gagal memuat data', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBayar = async (id, val) => {
    setUpdatingId(id);
    try {
      await updatePembayaran(id, val);
      notify(val === 'Sudah Dibayar' ? 'Pembayaran ditandai lunas' : 'Status pembayaran dibatalkan');
      fetchData(true);
    } catch (e) {
      notify(e.response?.data?.message || 'Gagal', 'error');
    } finally { setUpdatingId(null); }
  };

  // Statistik
  const totalTagih = list.length;
  const totalDitagih = list.filter(p => p.statusPenagihan === 'Ditagihkan').length;
  const totalLunas = list.filter(p => p.statusPembayaran === 'Sudah Dibayar').length;
  const totalBelum = list.filter(p => p.statusPembayaran === 'Belum Dibayar').length;
  const totalNominalDitagih = list.filter(p => p.statusPenagihan === 'Ditagihkan' || (p.statusPembayaran === 'Belum Dibayar' && p.statusPenagihan === 'Belum Ditagih'))
    .reduce((s, p) => s + (Number(p.totalDikirim) || 0), 0);
  const totalNominalLunas = list.filter(p => p.statusPembayaran === 'Sudah Dibayar')
    .reduce((s, p) => s + (Number(p.totalDikirim) || 0), 0);

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <RequestQuote sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
              Monitoring Penagihan Supplier
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pantau dan tindak lanjuti permintaan penagihan dari supplier.
            </Typography>
          </Box>
          <Tooltip title="Perbarui"><span>
            <IconButton size="small" onClick={() => fetchData(true)} disabled={refreshing || loading}
              sx={{ border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </span></Tooltip>
        </Stack>

        {/* Statistik */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
          <Paper sx={{ p: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }} elevation={0}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#e3f2fd', color: 'info.main', flexShrink: 0 }}>
                <Receipt />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{totalTagih}</Typography>
                <Typography variant="caption" color="text.secondary">Total Transaksi</Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }} elevation={0}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff3e0', color: 'warning.main', flexShrink: 0 }}>
                <PendingActions />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{totalDitagih}</Typography>
                <Typography variant="caption" color="text.secondary">Ditagihkan</Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }} elevation={0}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#ffebee', color: 'error.main', flexShrink: 0 }}>
                <Payments />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{totalBelum}</Typography>
                <Typography variant="caption" color="text.secondary">Belum Dibayar</Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }} elevation={0}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#e8f5e9', color: 'success.main', flexShrink: 0 }}>
                <CheckCircle />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{totalLunas}</Typography>
                <Typography variant="caption" color="text.secondary">Lunas</Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Ringkasan nominal */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">Total Nominal Ditagih (Belum Lunas)</Typography>
                <Typography variant="subtitle2" fontWeight={700} color="warning.main">{formatRp(totalNominalDitagih)}</Typography>
              </Box>
              <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Nominal Sudah Lunas</Typography>
                <Typography variant="subtitle2" fontWeight={700} color="success.main">{formatRp(totalNominalLunas)}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Filter */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField select size="small" label="Filter Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 200, bgcolor: '#fff' }}>
            <MenuItem value="">Semua</MenuItem>
            <MenuItem value="Ditagihkan">Ditagihkan (perlu dibayar)</MenuItem>
            <MenuItem value="Belum">Belum Ditagih</MenuItem>
            <MenuItem value="Lunas">Sudah Lunas</MenuItem>
          </TextField>
        </Stack>

        {/* Daftar */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : list.length === 0 ? (
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <RequestQuote sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Tidak ada data penagihan</Typography>
              <Typography variant="body2" color="text.secondary">
                Belum ada transaksi yang memerlukan tindak lanjut pembayaran.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1.5}>
            {list.map((item) => (
              <Card key={item.id} variant="outlined" sx={{ transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 2 } }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'flex-start' }}>
                    {/* Kiri: info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          <Receipt sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          #{item.id} · {item.namaSupplier}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <Person sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'text.secondary' }} />
                        <strong>{item.namaPenerima}</strong>
                        {item.jenisBantuan ? <span style={{ color: '#6b7280' }}> — {item.jenisBantuan}</span> : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.5 }}>
                        <LocationOn sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {[item.desaKelurahan, item.kecamatan].filter(Boolean).join(', ')}
                        {(item.namaTFL || item.namaAgr) ? ` · TFL: ${item.namaTFL || '-'} · AGR: ${item.namaAgr || '-'}` : ''}
                      </Typography>
                      {item.tanggalPenagihan && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.5 }}>
                          Ditagihkan: {formatDate(item.tanggalPenagihan)}
                        </Typography>
                      )}
                    </Box>

                    {/* Kanan: nominal + status + aksi */}
                    <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
                      <Typography variant="h6" fontWeight={700} color="primary.main">{formatRp(item.totalDikirim)}</Typography>
                      <Typography variant="caption" color="text.secondary">dari {formatRp(item.totalTransaksi)} (total transaksi)</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, justifyContent: { sm: 'flex-end' }, flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" icon={<LocalShipping sx={{ fontSize: 14 }} />} label={item.statusPengiriman} color={PENGIRIMAN_COLOR[item.statusPengiriman] || 'default'} />
                        <Chip size="small" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={item.statusPembayaran} color={BAYAR_COLOR[item.statusPembayaran] || 'default'} variant={item.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
                        {item.statusPenagihan === 'Ditagihkan' && item.statusPembayaran !== 'Sudah Dibayar' && (
                          <Chip size="small" color="warning" label="Perlu Dibayar" />
                        )}
                        {item.statusPembayaran === 'Sudah Dibayar' && (
                          <Chip size="small" color="success" icon={<DoneAll sx={{ fontSize: 14 }} />} label="Lunas" />
                        )}
                      </Stack>

                      {/* Aksi */}
                      <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: { sm: 'flex-end' } }}>
                        <Button size="small" variant="outlined" startIcon={<ArrowForward />}
                          onClick={() => navigate(`/supplier/pengiriman/${item.id}`)}
                          sx={{ textTransform: 'none' }}>
                          Detail
                        </Button>
                        {item.statusPembayaran !== 'Sudah Dibayar' && (
                          <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                            disabled={updatingId === item.id}
                            onClick={() => handleBayar(item.id, 'Sudah Dibayar')}
                            sx={{ textTransform: 'none' }}>
                            {updatingId === item.id ? '...' : 'Tandai Lunas'}
                          </Button>
                        )}
                        {item.statusPembayaran === 'Sudah Dibayar' && (
                          <Button size="small" variant="outlined" color="error"
                            disabled={updatingId === item.id}
                            onClick={() => handleBayar(item.id, 'Belum Dibayar')}
                            sx={{ textTransform: 'none' }}>
                            {updatingId === item.id ? '...' : 'Batalkan'}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* ADM Langkah 7 — Transfer Dana (bisa di-expand) */}
                  <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalance sx={{ fontSize: 18, color: 'info.main' }} />
                        <Typography variant="caption" fontWeight={700}>Transfer Dana ke Penerima (ADM Langkah 7)</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip size="small"
                          color={item.statusAdmTransferDana === 'Selesai' ? 'success' : item.statusAdmTransferDana === 'Menunggu Verifikasi' ? 'warning' : 'default'}
                          label={item.statusAdmTransferDana || 'Belum Dimulai'} />
                        <IconButton size="small" onClick={() => setExpandedAdm(expandedAdm === item.id ? null : item.id)}>
                          {expandedAdm === item.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Collapse in={expandedAdm === item.id}>
                      <Box sx={{ mt: 1 }}>
                        {(item.dokumenTransferDana || []).length > 0 ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Dokumen Transfer ({item.dokumenTransferDana.length}):
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                              {item.dokumenTransferDana.map((file, i) => (
                                <Button key={i} size="small" variant="outlined" startIcon={<Visibility />}
                                  sx={{ textTransform: 'none', fontSize: 11 }}
                                  onClick={() => openInAppFilePreview({ url: getAdmFileUrl(file, 'rab-progress-administrasi', BASE_URL), title: `Bukti Transfer ${i + 1}` })}>
                                  Bukti Transfer {i + 1}
                                </Button>
                              ))}
                            </Stack>
                            {item.statusAdmTransferDana === 'Selesai' && (
                              <Alert severity="success" sx={{ mt: 1, py: 0.5 }} icon={<CheckCircle fontSize="small" />}>
                                <Typography variant="caption" fontWeight={600}>Dana sudah ditransfer & divalidasi.</Typography>
                              </Alert>
                            )}
                            {item.statusAdmTransferDana === 'Menunggu Verifikasi' && (
                              <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                                <Typography variant="caption">Dokumen transfer sudah diupload, menunggu validasi.</Typography>
                              </Alert>
                            )}
                          </Box>
                        ) : (
                          <Alert severity="info" sx={{ py: 0.5 }} icon={<AccountBalance fontSize="small" />}>
                            <Typography variant="caption">Belum ada dokumen transfer dana. TFL/Admin belum mengupload bukti transfer.</Typography>
                          </Alert>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default MonitoringPenagihanSupplier;
